import BigJs from "big.js";

/**
 * Exact decimal arithmetic for quantities (invariant 4: never floats).
 *
 * A private constructor so this package's precision settings cannot leak into, or be changed by, any other
 * user of big.js. Division is the only operation that can produce an infinite expansion; it is carried to
 * DIVISION_SCALE places with banker's rounding and callers round further for storage or display.
 */
const DIVISION_SCALE = 20;
const D = BigJs();
D.DP = DIVISION_SCALE;
D.RM = D.roundHalfEven;
D.strict = true;

export type Decimal = BigJs.Big;

/** Canonical quantities are stored with this many fractional digits (Postgres `numeric(30, 9)`). */
export const STORAGE_SCALE = 9;

const DECIMAL_PATTERN = /^-?(\d+\.?\d*|\.\d+)$/;

export function isDecimalString(value: string): boolean {
  return DECIMAL_PATTERN.test(value.trim());
}

/** Parses a decimal string. Throws on anything that is not a plain decimal: no exponents, no floats. */
export function dec(value: string | Decimal): Decimal {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) {
    throw new RangeError(`Not a decimal number: "${value}"`);
  }
  return new D(trimmed);
}

export const ZERO: Decimal = new D("0");
export const ONE: Decimal = new D("1");
const HUNDRED: Decimal = new D("100");

export function sum(values: Iterable<string | Decimal>): Decimal {
  let total = ZERO;
  for (const value of values) total = total.plus(dec(value));
  return total;
}

/** Renders a decimal string from the database ("2.300000") in its shortest exact form ("2.3"). */
export function normalizeDecimal(value: string): string {
  return trimZeros(dec(value).toFixed());
}

/** Rounds to the storage scale and renders without exponent notation. */
export function toStorage(value: Decimal): string {
  return trimZeros(value.round(STORAGE_SCALE, D.roundHalfEven).toFixed(STORAGE_SCALE));
}

/** Rounds to a whole number of cents, half to even, and returns the count of cents. */
export function toCents(value: Decimal): Decimal {
  return value.times(HUNDRED).round(0, D.roundHalfEven);
}

/** Drops any fraction, toward zero. */
export function truncate(value: Decimal): Decimal {
  return value.round(0, D.roundDown);
}

/** Renders a value with at most `maxFractionDigits`, dropping trailing zeros. */
export function toFixedTrimmed(value: Decimal, maxFractionDigits: number): string {
  return trimZeros(value.round(maxFractionDigits, D.roundHalfEven).toFixed(maxFractionDigits));
}

function trimZeros(fixed: string): string {
  if (!fixed.includes(".")) return fixed === "-0" ? "0" : fixed;
  const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "-0" ? "0" : trimmed;
}
